import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useDispatch } from 'react-redux';
import { getUserByMultiRoles } from '../../../store/slices/userSlice';
import api from '../../../api/config';
import {
  EQUIPMENT_MODELS_RESOURCE,
  LEASES,
  MERCHANT_SERVICE_PROVIDERS,
} from '../../../api/endpoints';
import { extractList } from '../../../utils/response';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  TextField,
  Button,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';

const StatusDialog = ({
  open,
  selectedLead,
  newStatus,
  conditionalData = {},
  statusOptions = [],
  usersList = [],
  processingId,
  onClose,
  onStatusChange,
  onNewStatusChange,
  onConditionalChange,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userRoleName = (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase();
  const isFloorManager = userRoleName === 'floor manager';

  const [closerOptions, setCloserOptions] = useState([]);
  const [closersLoading, setClosersLoading] = useState(false);
  const [meetingAssigneeOptions, setMeetingAssigneeOptions] = useState([]);
  const [meetingAssigneesLoading, setMeetingAssigneesLoading] = useState(false);
  const [equipmentModelOptions, setEquipmentModelOptions] = useState([]);
  const [equipmentModelsLoading, setEquipmentModelsLoading] = useState(false);
  const [leaseOptions, setLeaseOptions] = useState([]);
  const [leaseOptionsLoading, setLeaseOptionsLoading] = useState(false);
  const [merchantServiceProviderOptions, setMerchantServiceProviderOptions] = useState([]);
  const [merchantServiceProviderOptionsLoading, setMerchantServiceProviderOptionsLoading] =
    useState(false);

  const selectedStatus = statusOptions.find((s) => String(s.id) === String(newStatus));
  const statusName = selectedStatus?.name;
  const normalizedStatusName = String(statusName || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  const isQualifiedStatus = (statusName || '').toLowerCase().trim() === 'qualified';
  const isSubmittedEquipmentStatus =
    normalizedStatusName.includes('submitted') &&
    normalizedStatusName.includes('equipment');
  // Covers "Submitted (LEASE)", "Submitted (MSP)", and "Submitted (MSP+LEASE)"
  const isSubmittedLeaseStatus =
    normalizedStatusName.includes('submitted') && normalizedStatusName.includes('lease');
  const isSubmittedMspStatus =
    normalizedStatusName.includes('submitted') &&
    (normalizedStatusName.includes('msp') ||
      normalizedStatusName.includes('merchant service provider'));
  const showAssigneeForFloorManager = isFloorManager && isQualifiedStatus;
  const assigneeMissing =
    showAssigneeForFloorManager && !(conditionalData?.assignees?.length > 0);
  const equipmentModelMissing =
    isSubmittedEquipmentStatus && !conditionalData?.equipment_model_id;
  const leaseMissing = isSubmittedLeaseStatus && !conditionalData?.lease_id;
  const merchantServiceProviderMissing =
    isSubmittedMspStatus && !conditionalData?.merchant_service_provider_id;

  useEffect(() => {
    if (!open || !isSubmittedEquipmentStatus || equipmentModelOptions.length > 0) return;
    let cancelled = false;
    setEquipmentModelsLoading(true);
    api
      .get(EQUIPMENT_MODELS_RESOURCE.LIST, { params: { paginate: 0 } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = extractList(data);
        setEquipmentModelOptions(
          (Array.isArray(list) ? list : []).map((item) => ({
            id: item?.id,
            name: item?.name || item?.title || '',
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setEquipmentModelOptions([]);
      })
      .finally(() => {
        if (!cancelled) setEquipmentModelsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isSubmittedEquipmentStatus, equipmentModelOptions.length]);

  useEffect(() => {
    if (!open || !isSubmittedLeaseStatus) {
      setLeaseOptionsLoading(false);
      return;
    }
    let cancelled = false;
    setLeaseOptionsLoading(true);
    api
      .get(LEASES.LIST, { params: { paginate: 0 } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = extractList(data);
        setLeaseOptions(
          (Array.isArray(list) ? list : []).map((item) => ({
            id: item?.id,
            name: item?.name || item?.title || '',
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setLeaseOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLeaseOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isSubmittedLeaseStatus, newStatus]);

  useEffect(() => {
    if (!open || !isSubmittedMspStatus) {
      setMerchantServiceProviderOptionsLoading(false);
      return;
    }
    let cancelled = false;
    setMerchantServiceProviderOptionsLoading(true);
    api
      .get(MERCHANT_SERVICE_PROVIDERS.LIST, { params: { paginate: 0 } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = extractList(data);
        setMerchantServiceProviderOptions(
          (Array.isArray(list) ? list : []).map((item) => ({
            id: item?.id,
            name: item?.name || item?.title || '',
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setMerchantServiceProviderOptions([]);
      })
      .finally(() => {
        if (!cancelled) setMerchantServiceProviderOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isSubmittedMspStatus, newStatus]);

  useEffect(() => {
    if (!showAssigneeForFloorManager) return;
    setClosersLoading(true);
    dispatch(getUserByMultiRoles({ 'roles[]': ['Closer'], paginate: 0 }))
      .unwrap()
      .then((res) => {
        const list = extractList(res?.data ?? res);
        setCloserOptions(list.map((u) => ({ id: u?.uuid ?? u?.id, name: u?.name || u?.username || '' })));
      })
      .catch(() => {})
      .finally(() => setClosersLoading(false));
  }, [showAssigneeForFloorManager, dispatch]);

  useEffect(() => {
    if (!open || statusName !== 'Meeting' || meetingAssigneeOptions.length > 0) return;
    let cancelled = false;
    setMeetingAssigneesLoading(true);
    dispatch(getUserByMultiRoles({ 'roles[]': ['Closer', 'Lead Gen'], paginate: 0 }))
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        const list = extractList(res?.data ?? res);
        setMeetingAssigneeOptions(
          list.map((u) => ({
            id: u?.uuid ?? u?.id,
            name: u?.name || u?.username || '',
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setMeetingAssigneeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setMeetingAssigneesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, statusName, meetingAssigneeOptions.length, dispatch]);

  const getSelectedAssignee = () => {
    return meetingAssigneeOptions.filter(
      (seller) => conditionalData?.assignees?.includes(seller.id)
    ) || [];
  };

  const timeZones = ["UTC", "EST", "PST", "CST", "GMT"];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Lead Status</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <Autocomplete
            options={statusOptions}
            getOptionLabel={(option) => option.name || ""}
            value={statusOptions.find((s) => String(s.id) === String(newStatus)) || null}
            onChange={(e, value) => {
              onNewStatusChange(value?.id || "");
              onConditionalChange('equipment_model_id', '');
              onConditionalChange('lease_id', '');
              onConditionalChange('merchant_service_provider_id', '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="Status" />
            )}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
          />
        </FormControl>

        {/* SELECT Status Fields */}
        {statusName !== 'Meeting' && statusName && (
          <Box sx={{ mt: 3, gap: 2, display: 'flex', flexDirection: 'column' }}>
            <TextField
              label="Date & Time"
              type="datetime-local"
              value={conditionalData?.date || ''}
              onChange={(e) => onConditionalChange('date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 16) }}
              fullWidth
            />
            <TextField
              label="Description"
              multiline
              rows={3}
              value={conditionalData?.description || ''}
              onChange={(e) => onConditionalChange('description', e.target.value)}
              placeholder="Reason / notes…"
              fullWidth
            />
            {isSubmittedEquipmentStatus && (
              <FormControl fullWidth>
                <Autocomplete
                  loading={equipmentModelsLoading}
                  options={equipmentModelOptions}
                  getOptionLabel={(option) => option.name || ''}
                  value={
                    equipmentModelOptions.find(
                      (item) =>
                        String(item.id) === String(conditionalData?.equipment_model_id)
                    ) ?? null
                  }
                  onChange={(e, value) =>
                    onConditionalChange('equipment_model_id', value?.id || '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Equipment"
                      placeholder="Search and select equipment"
                      required
                      error={equipmentModelMissing}
                      helperText={
                        equipmentModelMissing ? 'Equipment selection is required' : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {equipmentModelsLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  noOptionsText={
                    equipmentModelsLoading ? 'Loading...' : 'No equipment models found'
                  }
                />
              </FormControl>
            )}
            {isSubmittedLeaseStatus && (
              <FormControl fullWidth>
                <Autocomplete
                  loading={leaseOptionsLoading}
                  options={leaseOptions}
                  getOptionLabel={(option) => option.name || ''}
                  value={
                    leaseOptions.find(
                      (item) => String(item.id) === String(conditionalData?.lease_id)
                    ) ?? null
                  }
                  onChange={(e, value) =>
                    onConditionalChange('lease_id', value?.id || '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lease"
                      placeholder="Search and select lease"
                      required
                      error={leaseMissing}
                      helperText={leaseMissing ? 'Lease selection is required' : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {leaseOptionsLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  noOptionsText={leaseOptionsLoading ? 'Loading...' : 'No leases found'}
                />
              </FormControl>
            )}
            {isSubmittedMspStatus && (
              <FormControl fullWidth>
                <Autocomplete
                  loading={merchantServiceProviderOptionsLoading}
                  options={merchantServiceProviderOptions}
                  getOptionLabel={(option) => option.name || ''}
                  value={
                    merchantServiceProviderOptions.find(
                      (item) =>
                        String(item.id) ===
                        String(conditionalData?.merchant_service_provider_id)
                    ) ?? null
                  }
                  onChange={(e, value) =>
                    onConditionalChange(
                      'merchant_service_provider_id',
                      value?.id || ''
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Merchant Service Provider"
                      placeholder="Search and select merchant service provider"
                      required
                      error={merchantServiceProviderMissing}
                      helperText={
                        merchantServiceProviderMissing
                          ? 'Merchant service provider selection is required'
                          : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {merchantServiceProviderOptionsLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  noOptionsText={
                    merchantServiceProviderOptionsLoading
                      ? 'Loading...'
                      : 'No merchant service providers found'
                  }
                />
              </FormControl>
            )}
            {showAssigneeForFloorManager && (
              <FormControl fullWidth>
                <Autocomplete
                  loading={closersLoading}
                  options={closerOptions}
                  getOptionLabel={(option) => option.name || ''}
                  value={
                    closerOptions.find((o) => conditionalData?.assignees?.includes(o.id)) ?? null
                  }
                  onChange={(e, value) =>
                    onConditionalChange('assignees', value ? [value.id] : [])
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign To (Closer)"
                      placeholder="Search and select closer"
                      required
                      error={assigneeMissing}
                      helperText={assigneeMissing ? 'Assignee is required' : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {closersLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  noOptionsText={closersLoading ? 'Loading...' : 'No closers found'}
                />
              </FormControl>
            )}
          </Box>
        )}

        {/* MEETING Status Fields */}
        {statusName === 'Meeting' && (
          <Box sx={{ mt: 3, gap: 2, display: 'flex', flexDirection: 'column' }}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                options={meetingAssigneeOptions}
                getOptionLabel={(option) => option.name}
                value={getSelectedAssignee()}
                onChange={(e, value) =>
                  onConditionalChange(
                    "assignees",
                    value.map(v => v.id)
                  )
                }
                loading={meetingAssigneesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select assignee"
                    label="Select assignees"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={meetingAssigneesLoading ? 'Loading…' : 'No assignee found'}
              />
            </FormControl>

            <FormControl fullWidth>
              <Autocomplete
                options={timeZones}
                value={conditionalData?.timeZone || null}
                onChange={(e, value) => onConditionalChange("timeZone", value)}
                renderInput={(params) => (
                  <TextField {...params} label="Time Zone" />
                )}
              />
            </FormControl>
            
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              value={conditionalData?.startDateTime || ''}
              onChange={(e) => onConditionalChange('startDateTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="End Date & Time"
              type="datetime-local"
              value={conditionalData?.endDateTime || ''}
              onChange={(e) => onConditionalChange('endDateTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Meeting Description"
              multiline
              rows={3}
              value={conditionalData?.meetingDescription || ''}
              onChange={(e) => onConditionalChange('meetingDescription', e.target.value)}
              placeholder="Agenda, purpose…"
              fullWidth
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onStatusChange}
          variant="contained"
          disabled={
            !newStatus ||
            assigneeMissing ||
            equipmentModelMissing ||
            leaseMissing ||
            merchantServiceProviderMissing ||
            processingId === selectedLead?.id
          }
          startIcon={processingId === selectedLead?.id ? <CircularProgress size={20} /> : null}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusDialog;